/* global BigInt */
import React, {useEffect, useRef, useState} from 'react';
import {readConfirmedPreorderLifecycle, cancelConfirmedPreorder, readConfirmedPreorderCancellation} from '../../../http/orderApi';
import ConfirmedPreorderPayment from './ConfirmedPreorderPayment';

const unavailable = 'Не удалось проверить предзаказ. Проверьте статус или свяжитесь с менеджером.';
const storageError = 'Не удалось безопасно сохранить отмену. Не оплачивайте повторно; свяжитесь с менеджером.';
const fail = () => {throw new Error(unavailable);};
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const quantity = value => Number.isSafeInteger(value) && value >= 0 && value <= 2147483647;
const uuid = value => typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value);
const hash = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const record = (value, keys) => {
  if (!value || ![Object.prototype, null].includes(Object.getPrototypeOf(value)) || Reflect.ownKeys(value).length !== keys.length) fail();
  const copied = {};
  for (const key of keys) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !Object.hasOwn(descriptor, 'value') || !descriptor.enumerable) fail();
    copied[key] = descriptor.value;
  }
  return copied;
};
const identity = (checkout, acceptanceKey, principalId) => {
  const copied = record(checkout, ['schemaVersion', 'outcome', 'orderId', 'orderNumber', 'acceptedAt', 'termsSha256',
    'currency', 'totalAmountMinor', 'paymentMode', 'paymentMethodId', 'paymentState']);
  if (!id(principalId) || !uuid(acceptanceKey) || copied.schemaVersion !== 'confirmed-preorder-checkout-v1' || copied.outcome !== 'accepted' ||
    !id(copied.orderId) || !id(copied.orderNumber) || !hash(copied.termsSha256) || copied.currency !== 'RUB' ||
    typeof copied.totalAmountMinor !== 'string' || !/^[1-9][0-9]{0,15}$/.test(copied.totalAmountMinor) ||
    BigInt(copied.totalAmountMinor) > BigInt(Number.MAX_SAFE_INTEGER) || !id(copied.paymentMethodId) ||
    !['payment_now', 'payment_later'].includes(copied.paymentMode) || copied.paymentState !== 'unpaid' ||
    typeof copied.acceptedAt !== 'string' || !Number.isFinite(Date.parse(copied.acceptedAt)) ||
    new Date(copied.acceptedAt).toISOString() !== copied.acceptedAt) fail();
  return {checkout: copied, acceptanceKey, principalId,
    scope: JSON.stringify([principalId, acceptanceKey, ...Object.values(copied)]),
    slot: `yw.preorder.cancellation.v1:${principalId}:${acceptanceKey}`,
    command: {acceptanceKey, expectedTermsSha256: copied.termsSha256}};
};
const owned = (value, owner) => {
  if (value.orderId !== owner.checkout.orderId || value.orderNumber !== owner.checkout.orderNumber ||
    value.termsSha256 !== owner.checkout.termsSha256) fail();
};
const cancellation = (raw, owner, expectedKey) => {
  const value = record(raw, ['schemaVersion', 'outcome', 'orderId', 'orderNumber', 'termsSha256', 'cancellationKey']);
  if (value.schemaVersion !== 'confirmed-preorder-cancellation-v1' ||
    !['cancelled', 'cancel_pending', 'review_required'].includes(value.outcome) || !uuid(value.cancellationKey) ||
    (expectedKey && value.cancellationKey !== expectedKey)) fail();
  owned(value, owner); return value;
};
const readLifecycle = (raw, owner) => {
  const value = record(raw, ['schemaVersion', 'orderId', 'orderNumber', 'termsSha256', 'paymentOutcome', 'cancellation', 'fulfillment']);
  if (value.schemaVersion !== 'confirmed-preorder-lifecycle-v1' ||
    !['unpaid', 'processing', 'paid', 'review_required', 'payment_cancelled', 'refunded', 'partially_refunded'].includes(value.paymentOutcome)) fail();
  owned(value, owner);
  value.fulfillment = record(value.fulfillment, ['orderedQuantity', 'fulfilledQuantity', 'releasedQuantity', 'unfulfilledQuantity']);
  const f = value.fulfillment;
  if (!id(f.orderedQuantity) || !Object.values(f).every(quantity) ||
    f.orderedQuantity !== f.fulfilledQuantity + f.releasedQuantity + f.unfulfilledQuantity) fail();
  if (value.cancellation !== null) value.cancellation = cancellation(value.cancellation, owner);
  return value;
};
const markerFor = (owner, cancellationKey) => ({schemaVersion: 'confirmed-preorder-cancellation-marker-v1',
  principalId: owner.principalId, acceptanceKey: owner.acceptanceKey, termsSha256: owner.checkout.termsSha256, cancellationKey});
const pendingKey = owner => {
  const raw = localStorage.getItem(owner.slot);
  if (raw === null) return null;
  if (raw.length > 400) fail();
  const value = record(JSON.parse(raw), ['schemaVersion', 'principalId', 'acceptanceKey', 'termsSha256', 'cancellationKey']);
  if (!uuid(value.cancellationKey) || JSON.stringify(value) !== JSON.stringify(markerFor(owner, value.cancellationKey))) fail();
  return value.cancellationKey;
};
const newKey = () => {
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const persistKey = (owner, expectedKey) => {
  const previous = pendingKey(owner);
  if (previous !== expectedKey) fail();
  const key = expectedKey || newKey();
  localStorage.setItem(owner.slot, JSON.stringify(markerFor(owner, key)));
  if (pendingKey(owner) !== key) fail();
  return key;
};
const paymentMessages = {paid: 'Предзаказ оплачен.', processing: 'Оплата проверяется. Не оплачивайте повторно.',
  review_required: 'Предзаказ требует проверки менеджером. Повторно не оплачивайте.',
  payment_cancelled: 'Банк отменил оплату. Для дальнейших действий свяжитесь с менеджером.',
  refunded: 'Оплата возвращена.', partially_refunded: 'Часть оплаты возвращена.'};
const cancellationMessages = {cancelled: 'Предзаказ отменён.',
  cancel_pending: 'Отмена обрабатывается. Подтверждение банка может поступить позже; повторно не оплачивайте.',
  review_required: 'Отмена требует проверки менеджером. Сумма и факт возврата пока не подтверждены.'};
const canStartNew = value => value?.cancellation?.outcome === 'cancelled' &&
  ['unpaid', 'payment_cancelled', 'refunded'].includes(value.paymentOutcome) &&
  value.fulfillment.fulfilledQuantity === 0 && value.fulfillment.unfulfilledQuantity === 0 &&
  value.fulfillment.releasedQuantity === value.fulfillment.orderedQuantity;

// The lifecycle read is not a payment grant. The unchanged payment component
// prepares a fresh grant only on its explicit click. Cancellation keys remain
// durable, and no payment-started/spent marker is touched here.
export default function ConfirmedPreorderActions({checkout, acceptanceKey, principalId, onPaid, onNewPreorder, customerHistory = false,
  paymentButtonClassName}) {
  let owner = null;
  try {owner = identity(checkout, acceptanceKey, principalId);} catch (_) { /* fail closed below */ }
  const scope = owner?.scope || null;
  const currentScope = useRef(scope); currentScope.current = scope;
  const mounted = useRef(false), revision = useRef(0), busy = useRef(false), paidCallback = useRef(onPaid);
  paidCallback.current = onPaid;
  const [state, setState] = useState({scope: null, phase: 'loading'});
  const currentState = useRef(state); currentState.current = state;
  const valid = (captured, version) => mounted.current && currentScope.current === captured.scope && revision.current === version;
  const refreshCancellation = async (captured, version, localKey) => {
    const value = readLifecycle(await readConfirmedPreorderLifecycle(captured.command), captured);
    if (!valid(captured, version)) return;
    // An acknowledgement proves the cancellation command, not current money
    // or fulfillment. The fresh native lifecycle must agree on the same key.
    if (!value.cancellation || value.cancellation.cancellationKey !== localKey) fail();
    setState({scope: captured.scope, phase: 'ready', localKey, value});
  };
  const recover = async (captured, version, localKey, value) => {
    const raw = await readConfirmedPreorderCancellation({...captured.command, cancellationKey: localKey});
    if (!valid(captured, version)) return;
    let found;
    const descriptors = raw && Object.getOwnPropertyDescriptors(raw);
    if (descriptors && Object.hasOwn(descriptors, 'recoveryState')) {
      found = record(raw, ['schemaVersion', 'recoveryState', 'retryWithSameKey']);
      if (found.schemaVersion !== 'confirmed-preorder-cancellation-v1' || found.recoveryState !== 'not_found' || found.retryWithSameKey !== true) fail();
      setState({scope: captured.scope, phase: 'not_found', localKey, value});
    } else {
      found = cancellation(raw, captured, localKey);
      await refreshCancellation(captured, version, found.cancellationKey);
    }
  };
  const load = async (captured, background = false) => {
    if (!captured || busy.current) return;
    busy.current = true; const version = ++revision.current;
    const keepMounted = background && customerHistory && currentState.current.scope === captured.scope &&
      currentState.current.phase === 'ready' && !currentState.current.localKey;
    // A focus refresh must not unmount an active bank widget and invalidate
    // its callback. Owner changes and terminal server results still take effect.
    if (!keepMounted) setState({scope: captured.scope, phase: 'loading'});
    let localKey = null;
    try {
      try {localKey = pendingKey(captured);} catch (_) {throw new Error(storageError);}
      const value = readLifecycle(await readConfirmedPreorderLifecycle(captured.command), captured);
      if (!valid(captured, version)) return;
      if (value.cancellation) setState({scope: captured.scope, phase: 'ready', value, localKey});
      else if (localKey) await recover(captured, version, localKey, value);
      else setState({scope: captured.scope, phase: 'ready', value, localKey});
    } catch (error) {
      if (valid(captured, version)) {
        if (keepMounted) setState(previous => ({...previous, refreshError: true}));
        else setState({scope: captured.scope, phase: 'error', localKey,
          error: error.message === storageError ? storageError : unavailable});
      }
    } finally {if (valid(captured, version)) busy.current = false;}
  };
  useEffect(() => {
    mounted.current = true; busy.current = false; ++revision.current;
    if (owner) load(owner);
    else setState({scope: null, phase: 'error', error: unavailable});
    return () => {mounted.current = false; ++revision.current; busy.current = false;};
    // A descriptor-validated scope includes every immutable checkout field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  useEffect(() => {
    if (!customerHistory || !owner) return;
    let lastFocus = 0;
    const refresh = () => {
      if (!mounted.current || currentScope.current !== owner.scope || busy.current || Date.now() - lastFocus < 1000) return;
      lastFocus = Date.now(); load(owner, true);
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
    // Only a new owned receipt or a real focus event may refresh; no polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHistory, scope]);
  const submit = async () => {
    if (!owner || state.scope !== scope || state.phase !== 'confirm' || busy.current) return;
    busy.current = true; const captured = owner, version = ++revision.current;
    let localKey;
    try {localKey = persistKey(captured, state.localKey || null);} catch (_) {
      busy.current = false; setState({scope, phase: 'error', error: storageError}); return;
    }
    setState({...state, phase: 'sending', localKey});
    try {
      const result = await cancelConfirmedPreorder({...captured.command, cancellationKey: localKey});
      if (!valid(captured, version)) return;
      const found = cancellation(result, captured, localKey);
      await refreshCancellation(captured, version, found.cancellationKey);
    } catch (_) {
      if (!valid(captured, version)) return;
      try {await recover(captured, version, localKey, state.value);} catch (_) {
        if (valid(captured, version)) setState({scope: captured.scope, phase: 'error', localKey, error: unavailable});
      }
    } finally {if (valid(captured, version)) busy.current = false;}
  };
  const paid = () => {
    if (!owner || !mounted.current || currentScope.current !== owner.scope ||
      state.scope !== scope || state.phase !== 'ready' || busy.current) return;
    // Keep cancellation actions mounted while refreshing the native snapshot.
    if (typeof paidCallback.current === 'function') paidCallback.current();
    load(owner);
  };
  const startNew = () => {
    if (!owner || !mounted.current || currentScope.current !== owner.scope || currentState.current !== state ||
      state.scope !== scope || state.phase !== 'ready' || busy.current || !canStartNew(state.value) ||
      typeof onNewPreorder !== 'function') return;
    busy.current = true;
    try {onNewPreorder();}
    finally {if (mounted.current && currentScope.current === owner.scope) busy.current = false;}
  };
  if (!owner || state.scope !== scope || state.phase === 'error') return <div aria-live="polite">
    <p role="alert">{customerHistory ? 'Не удалось проверить предзаказ. Свяжитесь с менеджером.' : state.scope === scope ? state.error || unavailable : unavailable}</p>
    {owner && !customerHistory && <button type="button" onClick={() => load(owner)}>Проверить статус предзаказа</button>}
  </div>;
  if (state.phase === 'loading' || state.phase === 'sending') return <p role="status">
    {state.phase === 'sending' ? 'Проверяем результат отмены…' : 'Проверяем состояние предзаказа…'}</p>;
  const value = state.value, cancellationState = value.cancellation;
  const remaining = value.fulfillment.unfulfilledQuantity, partial = value.fulfillment.fulfilledQuantity > 0;
  const confirming = state.phase === 'confirm';
  return <section aria-label="Действия с предзаказом" aria-live="polite">
    {state.refreshError && <p role="alert">Не удалось обновить состояние предзаказа. Повторно не оплачивайте.</p>}
    {paymentMessages[value.paymentOutcome] && <p role="status">{paymentMessages[value.paymentOutcome]}</p>}
    {cancellationState ? <>
      <p role="status">{cancellationState.outcome === 'cancelled' && partial ?
        value.fulfillment.releasedQuantity > 0 ? 'Неотправленные товары отменены.' : 'Неотправленных товаров для отмены нет.' :
        cancellationMessages[cancellationState.outcome]}</p>
      {cancellationState.outcome === 'cancelled' && partial && <p>Уже отправленные товары не отменены. Для возврата свяжитесь с менеджером.</p>}
    </> : <>
      {state.phase === 'ready' && !state.localKey && value.paymentOutcome === 'unpaid' && remaining > 0 &&
        <ConfirmedPreorderPayment checkout={owner.checkout} acceptanceKey={acceptanceKey} principalId={principalId} onPaid={paid}
          autoStart={false} showStatusAction={!customerHistory} buttonText={customerHistory ? 'Оплатить' : undefined}
          buttonClassName={paymentButtonClassName}/>}
      {state.phase === 'not_found' && <p role="status">{customerHistory ? 'Состояние отмены уточняется. Свяжитесь с менеджером.' :
        'Отмена пока не найдена. Повторить её можно только с прежним номером запроса.'}</p>}
      {!customerHistory && (remaining > 0 ? confirming ? <div>
        <p>Подтвердите отмену {partial ? 'неотправленной части' : 'предзаказа'}. Это не подтверждение возврата денег.</p>
        {partial && <p>Сумму возврата определит менеджер. Уже отправленные товары здесь не отменяются.</p>}
        <button type="button" onClick={submit}>Подтвердить отмену</button>
        <button type="button" onClick={() => setState({...state, phase: state.localKey ? 'not_found' : 'ready'})}>Не отменять</button>
      </div> : <button type="button" onClick={() => setState({...state, phase: 'confirm'})}>
        {state.phase === 'not_found' ? 'Повторить отмену' : partial ? 'Отменить неотправленное' : 'Отменить предзаказ'}
      </button> : <p>Для возврата свяжитесь с менеджером. Неотправленных товаров для отмены нет.</p>)}
    </>}
    {!customerHistory && state.phase === 'ready' && canStartNew(value) && typeof onNewPreorder === 'function' &&
      <button type="button" onClick={startNew}>Оформить новый предзаказ</button>}
    {!customerHistory && !confirming && <button type="button" onClick={() => load(owner)}>Проверить статус предзаказа</button>}
  </section>;
}
